import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

export interface PatientExpressPayload {
  expressId: string;
  status: string;
  position?: number;
  estimatedTime?: number;
  doctorId?: string;
  doctorName?: string;
  sessionId?: string;
}

export type ExpressSocketConnectionState = 'disconnected' | 'connecting' | 'connected' | 'failed';

interface UseExpressSocketOptions {
  expressId?: string;
  token?: string;
  enabled?: boolean;
}

export interface RoomCreatedPayload {
  expressId: string;
  sessionId: string;
  roomId: string;
  status: 'ROOM_CREATED';
}

export interface ConsultationStartedPayload {
  expressId: string;
  sessionId: string;
  status: 'IN_PROGRESS';
}

export interface ConsultationFinishedPayload {
  expressId: string;
  sessionId: string;
  duration?: number;
  status: 'FINISHED';
}

interface UseExpressSocketReturn {
  connected: boolean;
  connectionState: ExpressSocketConnectionState;
  error: Error | null;
  connect: () => void;
  disconnect: () => void;
  onQueued: (handler: (data: PatientExpressPayload) => void) => () => void;
  onDoctorFound: (handler: (data: PatientExpressPayload) => void) => () => void;
  onDoctorAccepted: (handler: (data: PatientExpressPayload) => void) => () => void;
  onDoctorRejected: (handler: (data: PatientExpressPayload) => void) => () => void;
  onTimeout: (handler: (data: PatientExpressPayload) => void) => () => void;
  onCancelled: (handler: (data: PatientExpressPayload) => void) => () => void;
  onRoomCreated: (handler: (data: RoomCreatedPayload) => void) => () => void;
  onConsultationStarted: (handler: (data: ConsultationStartedPayload) => void) => () => void;
  onConsultationFinished: (handler: (data: ConsultationFinishedPayload) => void) => () => void;
}

function getSocketBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    if (!origin.includes('localhost') && !origin.includes('127.0.0.1')) {
      return origin;
    }
  }
  const envUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3000') as string;
  return envUrl.replace(/\/api(\/v\d+)?$/, '');
}

export function useExpressSocket({
  expressId,
  token,
  enabled = true,
}: UseExpressSocketOptions): UseExpressSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connectionState, setConnectionState] = useState<ExpressSocketConnectionState>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const handlersRef = useRef<Map<string, Set<(data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void>>>(new Map());

  const connect = useCallback(() => {
    if (!token || !enabled) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    setConnectionState('connecting');
    setError(null);

    const baseUrl = getSocketBaseUrl();
    console.info(`[PATIENT-SOCKET] Conectando ao WebSocket do paciente em: ${baseUrl}/express/patient`);

    const socket = io(`${baseUrl}/express/patient`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      setConnectionState('connected');
      setError(null);
      if (expressId) {
        socket.emit('express:join', { expressId });
      }
    });

    socket.on('disconnect', () => {
      setConnectionState('disconnected');
    });

    socket.on('connect_error', (err) => {
      setConnectionState('failed');
      setError(new Error(err.message));
    });

    socket.on('error', (data: { message: string }) => {
      setError(new Error(data.message));
    });

    const events = ['express:queued', 'express:doctor_found', 'express:doctor_accepted', 'express:doctor_rejected', 'express:timeout', 'express:cancelled'];
    events.forEach((event) => {
      socket.on(event, (data: PatientExpressPayload) => {
        const handlers = handlersRef.current.get(event);
        if (handlers) {
          handlers.forEach((handler) => handler(data));
        }
      });
    });

    socket.on('express:room_created', (data: RoomCreatedPayload) => {
      const handlers = handlersRef.current.get('express:room_created');
      if (handlers) {
        handlers.forEach((handler) => handler(data));
      }
    });

    socket.on('express:consultation_started', (data: ConsultationStartedPayload) => {
      const handlers = handlersRef.current.get('express:consultation_started');
      if (handlers) {
        handlers.forEach((handler) => handler(data));
      }
    });

    socket.on('express:consultation_finished', (data: ConsultationFinishedPayload) => {
      const handlers = handlersRef.current.get('express:consultation_finished');
      if (handlers) {
        handlers.forEach((handler) => handler(data));
      }
    });

    socketRef.current = socket;
  }, [token, enabled, expressId]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setConnectionState('disconnected');
  }, []);

  const onQueued = useCallback((handler: (data: PatientExpressPayload) => void) => {
    const event = 'express:queued';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
    return () => {
      handlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const onDoctorFound = useCallback((handler: (data: PatientExpressPayload) => void) => {
    const event = 'express:doctor_found';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
    return () => {
      handlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const onDoctorAccepted = useCallback((handler: (data: PatientExpressPayload) => void) => {
    const event = 'express:doctor_accepted';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
    return () => {
      handlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const onDoctorRejected = useCallback((handler: (data: PatientExpressPayload) => void) => {
    const event = 'express:doctor_rejected';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
    return () => {
      handlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const onTimeout = useCallback((handler: (data: PatientExpressPayload) => void) => {
    const event = 'express:timeout';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
    return () => {
      handlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const onCancelled = useCallback((handler: (data: PatientExpressPayload) => void) => {
    const event = 'express:cancelled';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
    return () => {
      handlersRef.current.get(event)?.delete(handler);
    };
  }, []);

  const onRoomCreated = useCallback((handler: (data: RoomCreatedPayload) => void) => {
    const event = 'express:room_created';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler as (data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void);
    return () => {
      handlersRef.current.get(event)?.delete(handler as (data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void);
    };
  }, []);

  const onConsultationStarted = useCallback((handler: (data: ConsultationStartedPayload) => void) => {
    const event = 'express:consultation_started';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler as (data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void);
    return () => {
      handlersRef.current.get(event)?.delete(handler as (data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void);
    };
  }, []);

  const onConsultationFinished = useCallback((handler: (data: ConsultationFinishedPayload) => void) => {
    const event = 'express:consultation_finished';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler as (data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void);
    return () => {
      handlersRef.current.get(event)?.delete(handler as (data: PatientExpressPayload | RoomCreatedPayload | ConsultationStartedPayload | ConsultationFinishedPayload) => void);
    };
  }, []);

  useEffect(() => {
    if (enabled && token) {
      connect();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [enabled, token, connect]);

  useEffect(() => {
    if (socketRef.current && connectionState === 'connected' && expressId) {
      socketRef.current.emit('express:join', { expressId });
    }
  }, [expressId, connectionState]);

  return {
    connected: connectionState === 'connected',
    connectionState,
    error,
    connect,
    disconnect,
    onQueued,
    onDoctorFound,
    onDoctorAccepted,
    onDoctorRejected,
    onTimeout,
    onCancelled,
    onRoomCreated,
    onConsultationStarted,
    onConsultationFinished,
  };
}
